<template>
  <div
    ref="wrapper"
    class="lg:fixed lg:h-full lg:left-0 flex flex-col w-full sidebar-transition group/wrapper lg:shadow-none lg:px-4 lg:pb-6 lg:bg-theme-paper lg:border-r lg:border-grey-300 dark:lg:border-theme-divider lg:border-opacity-20 lg:top-0 lg:z-20 lg:w-sidebar-nav lg:[&.collapsed]:w-sidebar-nav-collapsed lg:overflow-y-visible shrink-0 lg:[&.collapsed]:px-0"
  >
    <!-- Logo row -->
    <div class="hidden lg:flex items-center lg:h-[85px] border-b border-divider shrink-0">
      <div
        class="relative items-center justify-between hidden w-full pl-4 group/logos group-[&.collapsed]/wrapper:justify-center lg:flex group-[&.collapsed]/wrapper:pl-0"
      >
        <div
          class="content-transition group-[&.collapsed]/wrapper:flex group-[&.collapsed]/wrapper:items-center group-[&.collapsed]/wrapper:justify-center mr-auto group-[&.collapsed]/wrapper:mx-auto"
        >
          <ApplicationLogo class="justify-center" with-wordmark :is-collapsed="isCollapsed" />
        </div>
        <button
          type="button"
          @click="handleSidebarWidth"
          :aria-label="$t(isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')"
          :aria-expanded="!isCollapsed"
          class="flex items-center justify-center outline-none cursor-pointer button-transition shrink-0 hover:scale-110 active:scale-95 dark:text-white"
          :class="
            isCollapsed
              ? 'absolute -right-1/2 w-5 h-5 top-1/2 -translate-y-1/2'
              : 'w-5 h-5'
          "
        >
          <Icon type="menu-toggler" />
        </button>
      </div>
    </div>

    <!-- Nav items -->
    <nav
      :aria-label="$t('Admin navigation')"
      class="flex items-center flex-col self-stretch gap-1 w-full min-h-0 sidebar-transition group-[&.collapsed]/wrapper:w-16 lg:mx-auto lg:items-start lg:p-2 lg:h-full lg:justify-start lg:grow lg:shrink lg:basis-0 overflow-y-auto [scrollbar-width:thin]"
    >
      <template v-for="section in visibleSections" :key="section.key">
        <div
          v-if="section.title"
          class="items-center self-stretch justify-start hidden pt-3 pb-1 pl-2 lg:block"
        >
          <div
            class="text-teal-800 content-transition dark:text-theme-disabled text-[0.7875rem] font-bold uppercase leading-[1.125rem] group-[&.collapsed]/wrapper:opacity-0"
          >
            {{ $t(section.title) }}
          </div>
        </div>

        <component
          v-for="item in section.items"
          :key="item.label"
          :is="item.external ? 'a' : Link"
          :href="item.href"
          :target="item.external ? '_blank' : undefined"
          :rel="item.external ? 'noopener noreferrer' : undefined"
          :aria-current="isActive(item) ? 'page' : undefined"
          :class="isActive(item) ? css.bg_active : css.bg_inactive"
          class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[36px] relative px-6 menu-item-transition group overflow-hidden hover:scale-[1.02] active:scale-[0.98] lg:px-2"
        >
          <div
            class="flex justify-center items-center opacity-0 invisible left-0 truncate icon-slide-transition absolute group-[&.collapsed]/wrapper:opacity-100 group-[&.collapsed]/wrapper:visible group-[&.collapsed]/wrapper:translate-x-0 pl-3"
          >
            <Icon :type="item.icon" :width="24" :height="24" />
          </div>
          <div
            class="inline-flex truncate content-transition grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
          >
            <div
              class="text-xs font-semibold truncate whitespace-nowrap"
              :class="isActive(item) ? css.label_active : css.label"
            >
              {{ $t(item.label) }}
            </div>
          </div>
        </component>
      </template>
    </nav>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import Icon from '@/Icons/OptimizedIcons.vue';
import { Link, usePage } from '@inertiajs/vue3';

const page = usePage();

const permissions = computed(() => {
  const perms = page.props.auth?.permissions;

  if (Array.isArray(perms)) {
    return perms;
  }

  if (perms && typeof perms === 'object') {
    return Object.keys(perms);
  }

  return [];
});
const hasPermission = (permission) => permissions.value.includes(permission);
const isSuperUser = computed(() => Boolean(page.props.isSuperUser));

/**
 * @typedef {{ label: string, href: string, icon: string, active?: string, external?: boolean, visible?: boolean }} NavItem
 * @type {import('vue').ComputedRef<Array<{ key: string, title?: string, visible: boolean, items: NavItem[] }>>}
 */
const sections = computed(() => [
  {
    key: 'overview',
    visible: true,
    items: [{ label: 'Overview', href: route('admin-two'), active: 'admin-two', icon: 'nav-dashboard' }],
  },
  {
    key: 'users',
    title: 'Users',
    visible: hasPermission('h_users'),
    items: [
      { label: 'All Users', href: route('users.super.user'), active: 'users.super.user', icon: 'nav-candidates' },
      { label: 'All Companies', href: route('superuser.companies.all'), active: 'superuser.companies.all', icon: 'nav-property' },
      { label: 'Job Board Integrations', href: route('admin.integrations.jobboards'), active: 'admin.integrations.jobboards*', icon: 'nav-jobs' },
    ],
  },
  {
    key: 'marketing',
    title: 'Marketing',
    visible: hasPermission('h_marketing'),
    items: [
      { label: 'Category Job Count', href: route('superuser.category.jobs'), active: 'superuser.category.jobs', icon: 'nav-jobs' },
      { label: 'Organization Job Count', href: route('superuser.organization.jobs'), active: 'superuser.organization.jobs', icon: 'nav-jobs' },
      { label: 'Social Media Integrations', href: route('integrations.index'), active: 'integrations.*', icon: 'nav-jobs' },
      { label: 'Last Applications', href: route('last.applications'), active: 'last.applications', icon: 'nav-candidates' },
      { label: 'Companies & Last Jobs', href: route('companies.last.jobs'), active: 'companies.last.jobs', icon: 'nav-property' },
      { label: 'Job Advertising', href: route('job-advertising.index'), active: 'job-advertising.*', icon: 'nav-ai' },
      { label: 'Vonq Usage Analytics', href: route('vonq.usage.index'), active: 'vonq.usage.*', icon: 'nav-jobs' },
      { label: 'Candidate Interviews', href: route('admin.interviews.index'), active: 'admin.interviews.*', icon: 'nav-candidates' },
      { label: 'Job Distributions', href: route('admin.job-distributions.index'), active: 'admin.job-distributions.*', icon: 'nav-jobs' },
      { label: 'XML Feed Management', href: route('admin.feeds.index'), active: 'admin.feeds.*', icon: 'nav-jobs' },
    ],
  },
  {
    key: 'sales',
    title: 'Sales',
    visible: hasPermission('h_marketingadmin'),
    items: [{ label: 'Sales Leads', href: route('sales.leads'), active: 'sales.leads', icon: 'nav-candidates' }],
  },
  {
    key: 'analytics',
    title: 'Analytics',
    visible: true,
    items: [
      { label: 'Application Funnel Tracking', href: route('admin.tracking.index'), active: 'admin.tracking.*', icon: 'nav-ai' },
    ],
  },
  {
    key: 'system',
    title: 'System',
    visible: true,
    items: [
      { label: 'AI API Keys', href: route('admin.ai-api-keys.index'), active: 'admin.ai-api-keys.*', icon: 'nav-ai', visible: isSuperUser.value },
      { label: 'Partner API Keys', href: route('admin.integrations.partners'), active: 'admin.integrations.partners*', icon: 'nav-property', visible: hasPermission('superadmin') },
      { label: 'Hosco Accounts', href: route('admin.hosco-accounts.index'), active: 'admin.hosco-accounts.*', icon: 'nav-property', visible: isSuperUser.value },
      { label: 'DMARC Reports', href: route('admin.dmarc-reports.index'), active: 'admin.dmarc-reports.*', icon: 'nav-group' },
      { label: 'Sentry', href: 'https://nat-5q.sentry.io/issues/', icon: 'nav-ai', external: true, visible: hasPermission('h_dev') },
      { label: 'Horizon', href: route('horizon.index'), icon: 'nav-ai', external: true, visible: hasPermission('h_dev') },
    ],
  },
]);

const visibleSections = computed(() =>
  sections.value
    .filter((section) => section.visible)
    .map((section) => ({ ...section, items: section.items.filter((item) => item.visible !== false) }))
    .filter((section) => section.items.length > 0)
);

const isActive = (item) => Boolean(item.active) && route().current(item.active);

const css = {
  label_active: 'text-theme-primary font-semibold',
  label: 'text-theme-secondary',
  bg_active: 'bg-grey-100 dark:bg-white/10 rounded-lg',
  bg_inactive: 'rounded-lg hover:bg-grey-100 dark:hover:bg-white/5 transition-all',
};

const wrapper = ref(null);
const isCollapsed = ref(false);
const emit = defineEmits(['is-collapsed']);

const handleSidebarWidth = () => {
  if (window.innerWidth >= 1024) {
    wrapper.value.classList.toggle('collapsed');
    isCollapsed.value = !isCollapsed.value;
    localStorage.setItem('sidebar-admin2-collapsed', isCollapsed.value);
    emit('is-collapsed', isCollapsed.value);
  }
};

onMounted(() => {
  if (window.innerWidth >= 1024) {
    const saved = localStorage.getItem('sidebar-admin2-collapsed') === 'true';
    if (saved) {
      wrapper.value.classList.add('collapsed');
      isCollapsed.value = true;
    }
    emit('is-collapsed', isCollapsed.value);
  }
});
</script>

<style scoped>
.sidebar-transition {
  transition: all 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
  will-change: width, padding;
}

.content-transition {
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;
}

.icon-slide-transition {
  transition: opacity 350ms cubic-bezier(0.4, 0, 0.2, 1),
              transform 350ms cubic-bezier(0.4, 0, 0.2, 1),
              visibility 350ms;
  will-change: opacity, transform;
  transform: translateX(-100%) translateZ(0);
  backface-visibility: hidden;
}

.button-transition {
  transition: all 200ms cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.menu-item-transition {
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, background-color;
}
</style>
